import {
  deletePushSubscription,
  getPushPublicKey,
  registerPushSubscription,
} from '../services/pushSubscriptionService';

export const PUSH_SERVICE_WORKER_URL = '/push-sw.js';

export const isPushSupported = () => (
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window
);

export const readPushPermission = () => {
  if (typeof Notification === 'undefined') {
    return 'default';
  }
  return Notification.permission;
};

export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }
  return output;
};

export const toSubscriptionBody = (subscription) => ({
  endpoint: subscription?.endpoint,
  p256dh: subscription?.keys?.p256dh,
  auth: subscription?.keys?.auth,
  origin: window.location.origin,
});

const subscriptionBodyFrom = (subscription) => toSubscriptionBody(subscription.toJSON());

const waitForWorkerState = (worker, targetState) => new Promise((resolve, reject) => {
  if (!worker || worker.state === targetState) {
    resolve();
    return;
  }

  const onStateChange = () => {
    if (worker.state === targetState) {
      worker.removeEventListener('statechange', onStateChange);
      resolve();
    } else if (worker.state === 'redundant') {
      worker.removeEventListener('statechange', onStateChange);
      reject(new Error('Push service worker became redundant'));
    }
  };

  worker.addEventListener('statechange', onStateChange);
});

export const registerPushServiceWorker = async () => {
  const registration = await navigator.serviceWorker.register(PUSH_SERVICE_WORKER_URL);
  if (!registration.active) {
    const worker = registration.installing || registration.waiting;
    await waitForWorkerState(worker, 'activated');
  }

  return navigator.serviceWorker.ready;
};

export const syncPushSubscription = async (audience) => {
  if (!isPushSupported() || Notification.permission !== 'granted') {
    return false;
  }

  const registration = await registerPushServiceWorker();
  const subscription = await registration.pushManager.getSubscription();
  const body = subscription ? subscriptionBodyFrom(subscription) : null;
  if (!body?.endpoint || !body.p256dh || !body.auth) {
    return false;
  }

  await registerPushSubscription(audience, body);
  return true;
};

export const enablePushNotifications = async (audience) => {
  if (!isPushSupported()) {
    return 'unsupported';
  }

  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    return 'denied';
  }
  if (permission !== 'granted') {
    return 'dismissed';
  }

  let publicKey;
  try {
    publicKey = await getPushPublicKey(audience);
  } catch (error) {
    if (error?.response?.status === 404) {
      return 'unavailable';
    }
    throw error;
  }
  if (!publicKey) {
    return 'unavailable';
  }

  const registration = await registerPushServiceWorker();
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    await existing.unsubscribe();
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  const body = subscriptionBodyFrom(subscription);
  if (!body.endpoint || !body.p256dh || !body.auth) {
    return 'error';
  }

  await registerPushSubscription(audience, body);
  return 'enabled';
};

export const disablePushNotifications = async (audience) => {
  if (!isPushSupported()) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) {
    return;
  }

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  if (endpoint) {
    await deletePushSubscription(audience, endpoint);
  }
};
